import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { GetWatchlistV2ApiResponse, CurrencyConfigRes } from "src/store/features/api/generated";
import { useAppSelector } from "../store";
import { getRuntimeConfig } from "src/getRuntimeConfig";

interface CoinStatistics {
  price?: number;
  change?: number;
  marketCap?: number;
  tradingVolume?: number;
  supply?: number;
  supplyRatio?: number;
  description?: string;
  rank?: number;
  currencyConfig?: CurrencyConfigRes;
}

interface CandleChartState {
  time?: number;
  close?: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
}
export interface MarketsState {
  displayMarketHighlights: boolean;
  filterOption: string;
  type: string;
  limit: number;
  page: number;
  sector: string;
  initialWatchlist: GetWatchlistV2ApiResponse;
  triggerRefetch: boolean;
  coinStatistics: CoinStatistics;
  candleChartState: CandleChartState;
  ticker: string;
}

export const initialState: MarketsState = {
  displayMarketHighlights: true,
  filterOption: "All",
  type: "all",
  sector: "all",
  limit: 50,
  page: 1,
  initialWatchlist: [],
  triggerRefetch: false,
  coinStatistics: {},
  candleChartState: {
    high: 0,
    low: 0,
  },
  ticker: "",
};

export const fetchWatchlist = createAsyncThunk("dashboard/fetchByIdStatus", async (thunkAPI) => {
  axios({
    method: "get",
    url: `${getRuntimeConfig().BASE_URL}${getRuntimeConfig().SUB_PATH}/dashboard/watchlist`,
    responseType: "stream",
  });
});

const MarketsSlice = createSlice({
  name: "markets",
  initialState,
  reducers: {
    setInitialWatchlist: (
      state: MarketsState,
      action: PayloadAction<GetWatchlistV2ApiResponse>,
    ) => {
      state.initialWatchlist = action.payload;
    },
    resetMarketState: (state) => (state = initialState),
    toggleMarketHighlights: (state) => {
      state.displayMarketHighlights = !state.displayMarketHighlights;
    },
    toggleFilterOptions: (state: MarketsState, action: PayloadAction<string>) => {
      state.filterOption = action.payload;
    },
    setCoinStatistics: (state: MarketsState, action: PayloadAction<any>) => {
      state.coinStatistics = action.payload;
    },
    setCandleChartState: (state: MarketsState, action: PayloadAction<any>) => {
      state.candleChartState = action.payload;
    },
    setMarketTablePage: (state: MarketsState, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setMarketType: (
      state: MarketsState,
      action: PayloadAction<
        "All" | "Biggest gainers" | "Recently added" | "Watchlist" | "Portfolio" | "Alerts"
      >,
    ) => {
      const type = {
        All: "all",
        "Recently added": "recent",
        "Biggest gainers": "gainers",
        Portfolio: "portfolio",
        Watchlist: "watchlist",
        Alerts: "Alerts",
      };
      state.type = type[action.payload];
    },
    setMarketSector: (state: MarketsState, action: PayloadAction<string>) => {
      state.sector = action.payload;
    },
    setTriggerRefetch: (state: MarketsState, action: PayloadAction<boolean>) => {
      state.triggerRefetch = action.payload;
    },
    selectedCoinToken: (state: MarketsState, action: PayloadAction<string>) => {
      state.ticker = action.payload;
    },
  },
});

export default MarketsSlice;

export const {
  toggleMarketHighlights,
  toggleFilterOptions,
  resetMarketState,
  setMarketType,
  setMarketTablePage,
  setMarketSector,
  setCoinStatistics,
  setInitialWatchlist,
  setTriggerRefetch,
  setCandleChartState,
  selectedCoinToken,
} = MarketsSlice.actions;

export const useMarketsState = () => useAppSelector((state) => state.markets);
